// src/context/CartContext.jsx

import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartLoading, setIsCartLoading] = useState(true);

  useEffect(() => {
    const fetchFreshCartData = async () => {
      setIsCartLoading(true);
      const savedCart = localStorage.getItem('dietalase_cart');
      
      if (savedCart) {
        try {
          const parsedCart = JSON.parse(savedCart);
          const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

          const freshCartPromises = parsedCart.map(async (item) => {
            try {
              const response = await fetch(`${baseUrl}/api/products/${item.id}`);
              const result = await response.json();

              if (response.ok && result.data) {
                const freshProduct = result.data;
                
                // Menggunakan ?? agar angka 0 gram tidak berubah menjadi nilai default
                const freshWeight = freshProduct.shipping?.berat ?? freshProduct.product_weight ?? item.product_weight ?? 0;
                
                // Ambil persentase PPN murni dari database. Default 0 jika tidak ada data.
                let freshTaxRate = 0;
                if (freshProduct.ppn_persentase !== undefined && freshProduct.ppn_persentase !== null) {
                    freshTaxRate = parseFloat(freshProduct.ppn_persentase);
                } else if (freshProduct.specifications?.tax_info?.jenis) {
                    freshTaxRate = parseFloat(freshProduct.specifications.tax_info.jenis);
                }

                // [PERBAIKAN STOK] Ambil jumlah stok dari database
                const maxStock = Number(freshProduct.stock?.jumlah) || 0;

                return {
                  ...item,
                  name: freshProduct.product_name,
                  price: Number(freshProduct.product_price), 
                  product_weight: freshWeight, // Berat murni dari DB (bisa 0)
                  ppn_persentase: freshTaxRate, // PPN murni dari DB
                  maxStock: maxStock, // [BARU] Simpan batas maksimum stok
                  
                  product_image_path: freshProduct.product_image_path,
                  imageUrl: freshProduct.product_image_path,
                  image: freshProduct.product_image_path,
                  product_thumbnail: freshProduct.product_image_path,
                  product_images: freshProduct.product_images || [freshProduct.product_image_path]
                };
              }
              return item; 
            } catch (err) {
              console.error(`Gagal sync produk ID ${item.id}`, err);
              return item; 
            }
          });

          const updatedCart = await Promise.all(freshCartPromises);
          setCartItems(updatedCart);
        } catch (error) {
          console.error('Error membaca data keranjang:', error);
        }
      }
      setIsCartLoading(false);
    };

    fetchFreshCartData();
  }, []);

  useEffect(() => {
    if (!isCartLoading) {
      localStorage.setItem('dietalase_cart', JSON.stringify(cartItems));
    }
  }, [cartItems, isCartLoading]);

  const totalPrice = cartItems.reduce((total, item) => {
    const price = typeof item.price === 'number' ? item.price : 0;
    return total + price * item.quantity;
  }, 0);

  const addToCart = (product, qty = 1) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + qty } : item
        );
      } else {
        
        // Saat tambah produk, baca PPN persis seperti yang dikirim backend
        let taxRate = 0;
        if (product.ppn_persentase !== undefined && product.ppn_persentase !== null) {
            taxRate = parseFloat(product.ppn_persentase);
        } else if (product.specifications?.tax_info?.jenis) {
            taxRate = parseFloat(product.specifications.tax_info.jenis);
        }

        const taxNominal = (product.ppn && Number(product.ppn) > 0) 
            ? Number(product.ppn) 
            : Math.floor(Number(product.product_price) * (taxRate / 100));

        const validImage = product.product_image_path || product.imageUrl || product.product_thumbnail || '';
        
        // [PERBAIKAN STOK] Ambil jumlah stok dari product saat ditambah ke keranjang
        const maxStock = Number(product.stock?.jumlah) || 0;

        return [...prevItems, {
          id: product.id,
          name: product.product_name,
          price: Number(product.product_price), 
          quantity: qty, 
          
          product_image_path: validImage,
          imageUrl: validImage,
          image: validImage,
          product_images: product.product_images || [validImage],
          product_thumbnail: validImage,
          sku: product.sku,
          
          ppn_satuan: taxNominal,          
          ppn_persentase: taxRate, // Simpan PPN sesuai aslinya         
          maxStock: maxStock, // [BARU] Simpan batas maksimum stok
          
          is_PDN: product.is_pdn, 
          merchant_id: product.merchant_id, 
          merchant_name: product.merchant_name, 
          merchant_city: product.merchant_city, 
          merchant_province: product.merchant_province, 
          merchant_nik: product.merchant_nik, 
          merchant_npwp: product.merchant_npwp, 
          is_pkp: product.is_pkp, 
          merchant_score: product.merchant_score, 
          max_merchant_score: product.max_merchant_score, 
          merchant_type: product.merchant_type, 
          is_taxable: product.is_taxable !== undefined ? product.is_taxable : true,
          // Menggunakan ?? agar angka 0 gram tidak berubah
          product_weight: product.shipping?.berat ?? product.product_weight ?? 0,
          product_category: product.product_category || {"1": "General"},
          tags: product.tags || ["general"],
          product_description: product.product_description || 'Deskripsi tidak tersedia'
        }];
      }
    });
  };

  const increaseQuantity = (itemId) => {
    setCartItems((prevItems) => prevItems.map((item) => item.id === itemId ? { ...item, quantity: item.quantity + 1 } : item));
  };

  const decreaseQuantity = (itemId) => {
    setCartItems((prevItems) => prevItems.map((item) => item.id === itemId && item.quantity > 1 ? { ...item, quantity: item.quantity - 1 } : item).filter(item => item.quantity > 0));
  };

  const removeItem = (itemId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('dietalase_cart');
  };

  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const value = {
    cartItems,
    totalPrice,
    addToCart,
    increaseQuantity,
    decreaseQuantity,
    removeItem,
    clearCart,
    cartItemCount,
    isCartLoading
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};